import clsx from 'clsx'
import { useMemo } from 'react'
import * as React from 'react'
import { rem } from 'polished'
import styled from 'styled-components'
import { useIntl } from 'react-intl'
import TextLink from 'LuxKit/TextLink'
import Group from 'components/utils/Group'
import Heading from 'LuxKit/Typography/Heading'
import HeadingTextBlock from 'LuxKit/TextBlocks/HeadingTextBlock'
import BodyText from 'LuxKit/Typography/BodyText'
import Caption from 'LuxKit/Typography/Caption'
import { mediaQueryUp } from 'components/utils/breakpoint'

const NumberContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: ${props => props.theme.borderRadius.S};

  &.exclusive {
    background-color: ${props => props.theme.palette.product.exclusive.background};
  }

  &.black {
    background-color: ${props => props.theme.palette.neutral.surface.darkBackground};
  }

  &.variant-xs {
    height: ${rem(24)};
    padding: 0 ${rem(6)};
  }

  &.variant-small {
    padding: ${rem(6)};
  }

  &.variant-medium {
    padding: ${rem(6)};
  }

  &.variant-large {
    padding: ${rem(6)} ${rem(8)};
  }

  &.variant-xl {
    padding: ${rem(6)} ${rem(8)};
  }

  &.variant-xxl {
    padding: ${rem(6)} ${rem(8)};
  }

  &.square {
    padding: 0;
  }

  &.variant-xs.square {
    width: ${rem(24)};
    height: ${rem(24)};
  }

  &.variant-small.square {
    width: ${rem(36)};
    height: ${rem(36)};
  }

  &.variant-medium.square {
    width: ${rem(36)};
    height: ${rem(36)};
  }

  &.variant-large.square {
    width: ${rem(40)};
    height: ${rem(40)};
  }

  &.variant-xl.square {
    width: ${rem(40)};
    height: ${rem(40)};
  }

  &.variant-xxl.square {
    width: ${rem(48)};
    height: ${rem(48)};
  }
`

const LabelGroup = styled(Group)`
  min-width: 0;

  &.autoShowHide {
    display: none;

    ${mediaQueryUp.desktop} {
      display: flex;
    }
  }
`

const SubtitleText = styled(BodyText)`
  min-width: 0;
  overflow-wrap: break-word;
`

interface Props {
  link?: boolean
  rating?: number
  total?: number
  onlyNumber?: boolean | 'auto'
  inlineLabel?: boolean
  hideLink?: boolean
  variant?: 'xs' | 'small' | 'medium' | 'large' | 'xl' | 'xxl'
  ratingSubtitle?: string
  onReviewSubTitleClick?: () => void
  className?: string
  /**
   * Where the number rating will go
   * start or end of the rating count/source
   *
   * @default start
   */
  numberPosition?: 'start' | 'end'
  hideSubtitle?: boolean
  hideLabel?: boolean
  /**
   * @default 'horizontal'
   */
  layout?: 'horizontal' | 'vertical'
  /**
   * When true, the number container renders as a square
   */
  squareContainer?: boolean
}

function NumberRating(props: Props) {
  const {
    link,
    rating = 0,
    total = 0,
    onlyNumber = false,
    inlineLabel = false,
    hideLink = false,
    variant = 'xl',
    ratingSubtitle,
    onReviewSubTitleClick,
    numberPosition = 'start',
    className,
    hideSubtitle = false,
    hideLabel = false,
    layout = 'horizontal',
    squareContainer = false,
  } = props

  const intl = useIntl()

  const ratingLabel = useMemo(() => {
    if (rating >= 9.6) {
      return intl.formatMessage({
        id: 'STqWQbZD0VR3TSKhV0MZ',
        defaultMessage: 'Outstanding',
        description: 'Rating label for ratings 9.6 and above in the number rating section',
      })
    }
    if (rating >= 9 && rating < 9.6) {
      return intl.formatMessage({
        id: '_KKZeOZ-8VJB-dXmZl1B',
        defaultMessage: 'Exceptional',
        description: 'Rating label for ratings between 9.0 and 9.6 in the number rating section',
      })
    }
    if (rating >= 8.6 && rating < 9) {
      return intl.formatMessage({
        id: 'Rq0NfbHXlgcOwfMRxiWS',
        defaultMessage: 'Excellent',
        description: 'Rating label for ratings between 8.6 and 9.0 in the number rating section',
      })
    }
    if (rating >= 8 && rating < 8.6) {
      return intl.formatMessage({
        id: 'SIrbhaFbwuueAqp_1BzG',
        defaultMessage: 'Very Good',
        description: 'Rating label for ratings between 8.0 and 8.6 in the number rating section',
      })
    }
    if (rating >= 7 && rating < 8) {
      return intl.formatMessage({
        id: 'YGL7udKDj6M6vy60xAH3',
        defaultMessage: 'Good',
        description: 'Rating label for ratings between 7.0 and 8.0 in the number rating section',
      })
    }
    return intl.formatMessage({
      id: 'JPfuczY4MjWIZO4euUu1',
      defaultMessage: 'Rating',
      description: 'Default accessible label for the star rating display on offer and review cards',
    })
  }, [rating, intl])

  const subtitle = useMemo(() => {
    if (hideSubtitle) return null
    return ratingSubtitle || intl.formatMessage({
      id: 'ftg8D1wwZfE7WY-6WaIO',
      defaultMessage: '{count, plural, one {# review} other {# reviews}}',
      description: 'Number of reviews for rating display in the guest reviews section',
    }, { count: total })
  }, [ratingSubtitle, total, hideSubtitle, intl])
  const ratingText = useMemo(() => {
    return intl.formatNumber(
      rating,
      rating === 10
        ? { maximumFractionDigits: 0 }
        : { minimumFractionDigits: 1, maximumFractionDigits: 1 },
    )
  }, [rating, intl])

  const isPremium = rating >= 7

  const ratingLabelElement = variant === 'xxl' ? (
    <HeadingTextBlock variant="heading5" colour="normal-contrast">
      {ratingLabel}
    </HeadingTextBlock>
  ) : (
    <BodyText
      variant={variant === 'xl' ? 'medium' : 'small'}
      weight="bold"
      colour="normal-contrast"
    >
      {ratingLabel}
    </BodyText>
  )

  let ratingElement: React.ReactNode
  if (variant === 'xxl') {
    ratingElement = <Heading variant="heading1" as="h2" colour={isPremium ? 'exclusive-contrast' : 'dark-contrast'}>{ratingText}</Heading>
  } else if (variant === 'xl') {
    ratingElement = <Heading variant="heading2" colour={isPremium ? 'exclusive-contrast' : 'dark-contrast'}>{ratingText}</Heading>
  } else if (variant === 'large') {
    ratingElement = <BodyText variant="large" colour={isPremium ? 'exclusive-contrast' : 'dark-contrast'}>{ratingText}</BodyText>
  } else if (variant === 'medium') {
    ratingElement = <Heading variant="heading6" colour={isPremium ? 'exclusive-contrast' : 'dark-contrast'}>{ratingText}</Heading>
  } else if (variant === 'small') {
    ratingElement = <Caption variant="large" weight="bold" colour={isPremium ? 'exclusive-contrast' : 'dark-contrast'}>{ratingText}</Caption>
  } else {
    ratingElement = <BodyText variant="small" weight="bold" colour={isPremium ? 'exclusive-contrast' : 'dark-contrast'}>{ratingText}</BodyText>
  }

  return (
    <Group
      direction={layout === 'vertical' ? 'vertical' : numberPosition === 'start' ? 'horizontal' : 'horizontal-reverse'}
      verticalAlign={layout === 'vertical' ? undefined : 'center'}
      horizontalAlign={squareContainer ? 'start' : undefined}
      gap={layout === 'vertical' ? 4 : 8}
      className={className}
      data-testid="number-rating"
    >
      <NumberContainer
        className={clsx(`variant-${variant}`, {
          exclusive: isPremium,
          black: !isPremium,
          square: squareContainer,
        })}
        data-testid="number-container"
      >
        {ratingElement}
      </NumberContainer>
      {onlyNumber !== true && (
        <LabelGroup
          direction={inlineLabel ? 'horizontal' : 'vertical'}
          verticalAlign={inlineLabel ? 'center' : undefined}
          horizontalAlign={inlineLabel ? undefined : numberPosition === 'end' ? 'end' : 'start'}
          gap={inlineLabel ? 4 : undefined}
          className={clsx({ autoShowHide: onlyNumber === 'auto' })}
        >
          {!hideLabel && ratingLabelElement}
          {!hideLink && <>
            {link && (
              <TextLink
                size={variant === 'xxl' ? 'medium' : 'small'}
                href="#customer-reviews"
                onClick={onReviewSubTitleClick}
                weight="regular"
              >
                {subtitle}
              </TextLink>
            )}
            {!link && <SubtitleText
              variant={variant === 'xxl' ? 'medium' : 'small'}
              colour="light-foreground-alt"
            >
              {subtitle}
            </SubtitleText>}
          </>}
        </LabelGroup>
      )}
    </Group>
  )
}

export default NumberRating
